import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import TransferPortal from './TransferPortal';
import PortalIntelTab from '../../components/portal/PortalIntelTab';
import { useTeam } from '../../lib/teamConfig';
import { C, Pill } from '../../components/design-system/primitives';
import CBBHeadshot from '../../components/CBBHeadshot';
import { fetchTeamHeadshots } from '../../lib/cbbHeadshotService';
import useGuidedTour from '../../hooks/useGuidedTour';
import GuidedTooltip from '../../components/design-system/GuidedTooltip';
import {
  Users,
  DollarSign,
  Target,
  Shield,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  Repeat,
  Briefcase,
  ChevronRight,
  ChevronDown,
  Loader2,
  Star,
  Zap,
  RefreshCw,
  Eye,
} from 'lucide-react';

const GameFilmDashboard = lazy(() => import('../baseball/GameFilmDashboard'));

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const REVENUE_SHARE_CAP = 22_000_000;
const SCHOLARSHIP_LIMIT = 13;

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'roster', label: 'Roster', icon: Users },
  { id: 'diamond-eye', label: 'Diamond Eye', icon: Eye },
  { id: 'portal', label: 'Portal Intel', icon: Repeat },
  { id: 'nil', label: 'NIL', icon: DollarSign },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
];

// ESPN MBB team IDs for headshot lookup
const ESPN_MBB_TEAM_IDS = {
  'clemson': 228, 'duke': 150, 'north carolina': 153, 'kentucky': 96, 'kansas': 2305,
  'gonzaga': 2250, 'houston': 248, 'auburn': 2, 'iowa state': 66, 'tennessee': 2633,
  'purdue': 2509, 'uconn': 41, 'connecticut': 41, 'alabama': 333, 'arizona': 12,
  'baylor': 239, 'creighton': 156, 'illinois': 356, 'marquette': 269, 'michigan state': 127,
  'texas tech': 2641, 'wisconsin': 275, 'florida': 57, 'indiana': 84, 'iowa': 2294,
  'louisville': 97, 'michigan': 130, 'ohio state': 194, 'oklahoma': 201, 'oregon': 2483,
  'pittsburgh': 221, 'san diego state': 21, 'st johns': 2599, "st. john's": 2599,
  'syracuse': 183, 'texas': 251, 'ucla': 26, 'usc': 30, 'villanova': 222,
  'virginia': 258, 'west virginia': 277, 'xavier': 2752, 'arkansas': 8,
  'colorado': 38, 'dayton': 2248, 'drake': 2181, 'memphis': 235, 'mississippi state': 344,
  'nc state': 152, 'north carolina state': 152, 'notre dame': 87, 'providence': 2507,
  'rutgers': 164, 'seton hall': 2550, 'south carolina': 2579, 'stanford': 24,
  'tcu': 2628, 'vanderbilt': 238, 'virginia tech': 259, 'wake forest': 154,
  'maryland': 120, 'minnesota': 135, 'nebraska': 158, 'northwestern': 77,
  'penn state': 213, 'georgia': 61, 'lsu': 99, 'missouri': 142, 'ole miss': 145,
  'mississippi': 145, 'texas a&m': 245, 'cincinnati': 2132, 'ucf': 2116,
  'brown': 225, 'harvard': 108, 'yale': 43, 'princeton': 163, 'columbia': 171,
  'cornell': 172, 'dartmouth': 159, 'penn': 219, 'rhode island': 227,
};

function getEspnTeamId(teamName) {
  if (!teamName) return null;
  const key = teamName.toLowerCase().trim();
  return ESPN_MBB_TEAM_IDS[key]
    || ESPN_MBB_TEAM_IDS[Object.keys(ESPN_MBB_TEAM_IDS).find(k => key.startsWith(k) || k.startsWith(key))]
    || null;
}


// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function formatCurrency(value) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function KPICard({ label, value, subtext, icon: Icon, trend }) {
  return (
    <div
      style={{
        background: C.s1,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: '16px 16px',
        transition: 'all .3s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontSize: 9, fontWeight: 600, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>
          {label}
        </span>
        <Icon size={14} style={{ color: C.t3 }} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 200, color: C.text, letterSpacing: -1.5, fontFamily: "'Inter', system-ui" }}>
        {value}
      </div>
      {subtext && (
        <div style={{ fontSize: 11, marginTop: 4, color: C.t2 }}>
          {trend === 'up' && <TrendingUp size={10} className="inline mr-1" style={{ color: C.elite }} />}
          {trend === 'down' && <TrendingDown size={10} className="inline mr-1" style={{ color: C.dev }} />}
          {subtext}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TAB CONTENT COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function OverviewTab({ summary, onNavigate }) {
  if (!summary) return null;
  const { rosterOverview = {}, financialSummary = {}, portalSummary = {}, complianceScore = {}, teamRecord } = summary;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div data-tour="gm-kpi-cards" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Roster"
          value={`${rosterOverview.scholarshipPlayers}/${SCHOLARSHIP_LIMIT}`}
          subtext={`${rosterOverview.totalPlayers} total (${rosterOverview.walkOns} walk-ons)`}
          icon={Users}
        />
        <KPICard
          label="Cap Utilization"
          value={`${financialSummary.capUtilization}%`}
          subtext={`${formatCurrency(financialSummary.availableCap)} available`}
          icon={DollarSign}
        />
        <KPICard
          label="Portal Targets"
          value={portalSummary.activeTargets || '--'}
          subtext={`Needs: ${(portalSummary.positionNeeds || []).join(', ') || 'None identified'}`}
          icon={Target}
        />
        <KPICard
          label="Compliance"
          value={`${complianceScore.overall}%`}
          subtext="All categories passing"
          icon={Shield}
          trend="up"
        />
      </div>

      {/* Team Record (if available) */}
      {teamRecord && !teamRecord.noData && (
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
            Season Record
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: `${teamRecord.wins}-${teamRecord.losses}`, l: 'RECORD' },
              { v: teamRecord.avgPoints, l: 'PPG' },
              { v: teamRecord.avgOppPoints, l: 'OPP PPG' },
              { v: `${teamRecord.pointDiff >= 0 ? '+' : ''}${teamRecord.pointDiff}`, l: 'MARGIN', c: teamRecord.pointDiff >= 0 ? C.elite : C.dev },
            ].map(s => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 200, color: s.c || C.text, letterSpacing: -1 }}>{s.v}</div>
                <div style={{ fontSize: 8, color: C.t3, letterSpacing: 2, marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cap Sheet Summary Bar */}
      <div data-tour="gm-cap-bar" style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>
            Revenue Share Cap
          </span>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.t2 }}>
            {formatCurrency(financialSummary.estimatedTotalCost)} / {formatCurrency(REVENUE_SHARE_CAP)}
          </span>
        </div>
        <div style={{ height: 3, background: C.border, borderRadius: 2, position: 'relative' }}>
          <div style={{
            position: 'absolute', top: -0.5, left: 0, height: 4,
            width: `${Math.min(financialSummary.capUtilization, 100)}%`,
            background: financialSummary.capUtilization > 90
              ? `linear-gradient(90deg, ${C.dev}, ${C.dev})`
              : `linear-gradient(90deg, ${C.goldDim}, ${C.gold})`,
            borderRadius: 2,
            transition: 'width 1.2s cubic-bezier(.16,1,.3,1)',
          }}>
            <div style={{
              position: 'absolute', right: -4, top: -4.5,
              width: 12, height: 12, borderRadius: '50%',
              background: C.bg, border: `2px solid ${financialSummary.capUtilization > 90 ? C.dev : C.gold}`,
              boxShadow: `0 0 8px ${C.gold}33`,
            }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
          <span style={{ fontSize: 10, color: C.t3 }}>
            Scholarships: {formatCurrency(financialSummary.scholarshipCost)}
          </span>
          <span style={{ fontSize: 10, color: C.t3 }}>
            NIL: {formatCurrency(financialSummary.nilSpend)} | Rev Share: {formatCurrency(financialSummary.revenueShareAllocated)}
          </span>
        </div>
      </div>

      {/* Quick Access Grid */}
      <div data-tour="gm-quick-access" className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { label: 'Roster & Evaluations', desc: 'Player stats, AI grades & scouting', icon: Users, tab: 'roster' },
          { label: 'Diamond Eye', desc: 'Game film analysis & video intel', icon: Eye, tab: 'diamond-eye' },
          { label: 'Portal Intelligence', desc: 'Transfer portal candidates', icon: Repeat, tab: 'portal' },
          { label: 'NIL Management', desc: 'Deals, valuations & budget', icon: DollarSign, tab: 'nil' },
          { label: 'Compliance Center', desc: 'NCAA, NIL & academic status', icon: Shield, tab: 'compliance' },
          { label: 'Team Analytics', desc: 'Performance & advanced stats', icon: BarChart3, tab: 'analytics' },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => onNavigate?.(item.tab)}
            style={{
              textAlign: 'left',
              background: C.s1,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              padding: '14px 16px',
              cursor: 'pointer',
              transition: 'all .3s',
              display: 'block',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <item.icon size={13} style={{ color: C.gold }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{item.label}</span>
              <ChevronRight size={11} style={{ marginLeft: 'auto', color: C.t3 }} />
            </div>
            <span style={{ fontSize: 10, color: C.t2 }}>{item.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function RosterTab({
  roster, loading, headshotMap = {}, onPlayerClick,
  evaluationsData, evaluationsLoading, evaluationsError,
  selectedTeam, onEvalRefresh,
}) {
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  // Build eval lookup map by playerName
  const evalMap = useMemo(() => {
    if (!evaluationsData?.evaluations) return {};
    const map = {};
    evaluationsData.evaluations.forEach(e => { map[e.playerName] = e; });
    return map;
  }, [evaluationsData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[#999999]" />
      </div>
    );
  }

  const players = roster?.roster || [];
  const totals = roster?.totals || {};
  const evalSummary = evaluationsData?.evaluationSummary;
  const COL_COUNT = 16;

  // Compute position breakdown with ESPN enrichment
  const positionCounts = {};
  players.forEach(p => {
    const nameKey = (p.playerName || '').toLowerCase().trim();
    const espnData = headshotMap[nameKey] || {};
    const pos = (p.position && p.position !== '--')
      ? p.position
      : (espnData.position || 'Unknown');
    positionCounts[pos] = (positionCounts[pos] || 0) + 1;
  });

  return (
    <div className="space-y-4">
      {/* Evaluation KPI Strip */}
      {evalSummary && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>AI Evaluations</span>
              <span style={{ fontSize: 10, fontFamily: 'monospace', color: C.t3 }}>Gemini 3 Flash</span>
            </div>
            <Pill onClick={onEvalRefresh}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <RefreshCw size={10} />
                Regenerate
              </span>
            </Pill>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" style={{ marginBottom: 16 }}>
            <KPICard label="Avg Fit Score" value={evalSummary.avgFitScore} subtext={`${evalSummary.totalPlayers} players evaluated`} icon={Target} />
            <KPICard label="Star Players" value={evalSummary.starPlayers} subtext="Primary options" icon={Star} />
            <KPICard label="Draft Prospects" value={evalSummary.draftProspects} subtext="NBA potential" icon={TrendingUp} />
            <KPICard label="Portal Risks" value={evalSummary.portalRisks} subtext="High risk players" icon={AlertTriangle} trend={evalSummary.portalRisks > 0 ? 'down' : 'up'} />
            <KPICard label="Evaluated" value={new Date(evalSummary.generatedAt).toLocaleDateString()} subtext={new Date(evalSummary.generatedAt).toLocaleTimeString()} icon={Clock} />
          </div>
        </div>
      )}

      {/* Evaluations loading / not-yet-generated banners */}
      {evaluationsLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: `${C.gold}08`, border: `1px solid ${C.goldDim}` }}>
          <Loader2 size={14} className="animate-spin" style={{ color: C.gold }} />
          <span style={{ fontSize: 11, color: C.t2 }}>Generating AI evaluations for {selectedTeam}...</span>
        </div>
      )}
      {evaluationsError && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderRadius: 10, background: `${C.dev}08`, border: `1px solid ${C.dev}22` }}>
          <span style={{ fontSize: 11, color: C.dev }}>{evaluationsError}</span>
          <Pill onClick={onEvalRefresh} color={C.dev}>Retry</Pill>
        </div>
      )}
      {!evaluationsData && !evaluationsLoading && !evaluationsError && selectedTeam && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 10, background: C.s1, border: `1px solid ${C.border}` }}>
          <div>
            <span style={{ fontSize: 12, color: C.text }}>AI Evaluations Available</span>
            <span style={{ fontSize: 10, color: C.t3, marginLeft: 8 }}>Generate scouting grades, fit scores & portal risk analysis</span>
          </div>
          <Pill onClick={onEvalRefresh}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Star size={10} />
              Generate Evaluations
            </span>
          </Pill>
        </div>
      )}

      {/* Position Depth Summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {Object.entries(positionCounts).sort((a, b) => b[1] - a[1]).map(([pos, count]) => (
          <span
            key={pos}
            style={{
              padding: '4px 12px', borderRadius: 100, fontSize: 10, fontFamily: 'monospace', fontWeight: 500,
              background: C.goldDim, color: C.gold, letterSpacing: 0.3,
            }}
          >
            {pos}: {count}
          </span>
        ))}
        <span style={{
          padding: '4px 12px', borderRadius: 100, fontSize: 10, fontFamily: 'monospace',
          background: C.s2, color: C.t2,
        }}>
          Total: {players.length}
        </span>
      </div>

      {/* Enriched Roster Table */}
      <div className="overflow-x-auto" style={{ borderRadius: 16, border: `1px solid ${C.border}` }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: C.g08 }}>
              {['', 'Player', 'Pos', '#', 'PPG', 'RPG', 'APG', 'MPG', 'GP', 'Grade', 'Fit', 'Role', 'Risk', 'Scholarship', 'NIL Value', 'Total Cost'].map(h => (
                <th
                  key={h || '__photo'}
                  style={{
                    padding: '10px 12px', textAlign: 'left', fontSize: 9, fontWeight: 600,
                    letterSpacing: 2, textTransform: 'uppercase', color: C.t3,
                    ...(h === '' ? { width: 48, padding: '10px 6px 10px 12px' } : {}),
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} style={{ padding: '32px 12px', textAlign: 'center', fontSize: 12, color: C.t2 }}>
                  Select a team to view roster data
                </td>
              </tr>
            ) : (
              players.map((player, i) => {
                const nameKey = (player.playerName || '').toLowerCase().trim();
                const espnData = headshotMap[nameKey] || {};
                const espnId = espnData.espnId || null;
                const position = (player.position && player.position !== '--')
                  ? player.position
                  : (espnData.position || '--');
                const evalData = evalMap[player.playerName];
                const ev = evalData?.evaluation;
                const evStats = evalData?.stats;
                const isExpanded = expandedPlayer === player.playerName;
                const fitColor = ev ? (ev.fitScore >= 80 ? C.elite : ev.fitScore >= 60 ? C.gold : C.dev) : C.t3;

                return (
                  <React.Fragment key={`${player.playerName}-${i}`}>
                    <tr
                      onClick={() => setExpandedPlayer(isExpanded ? null : player.playerName)}
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        background: isExpanded ? C.goldDim : (i % 2 === 0 ? C.s1 : 'transparent'),
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.goldDim; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isExpanded ? C.goldDim : (i % 2 === 0 ? C.s1 : 'transparent'); }}
                    >
                      <td style={{ padding: '4px 6px 4px 12px', verticalAlign: 'middle', width: 48 }}>
                        <CBBHeadshot
                          espnId={espnId}
                          playerName={player.playerName}
                          size="SMALL"
                          gender="mens"
                          style={{ borderRadius: '50%', width: 36, height: 36 }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: C.text, fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {player.playerName}
                          <ChevronDown size={10} style={{ color: C.t3, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .3s', flexShrink: 0 }} />
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t2 }}>
                        {position}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t3 }}>
                        {player.jersey}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 13, color: C.text }}>
                        {player.stats.ppg.toFixed(1)}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 13, color: C.t2 }}>
                        {player.stats.rpg.toFixed(1)}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 13, color: C.t2 }}>
                        {player.stats.apg.toFixed(1)}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t2 }}>
                        {player.stats.mpg.toFixed(1)}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t3 }}>
                        {player.stats.gamesPlayed}
                      </td>
                      {/* Evaluation columns */}
                      <td style={{ padding: '8px 8px' }}>
                        {ev ? <GradeBadge grade={ev.overallGrade} /> : <span style={{ fontSize: 11, color: C.t3 }}>--</span>}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        {ev ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 40, height: 3, background: C.s3, borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${ev.fitScore}%`, background: fitColor, borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 500, color: fitColor }}>{ev.fitScore}</span>
                          </div>
                        ) : <span style={{ fontSize: 11, color: C.t3 }}>--</span>}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        {ev ? <RoleBadge role={ev.role} /> : <span style={{ fontSize: 11, color: C.t3 }}>--</span>}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        {ev ? <PortalRiskBadge risk={ev.portalRisk} /> : <span style={{ fontSize: 11, color: C.t3 }}>--</span>}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
                          background: player.financials.scholarship === 'Full' ? C.goldDim : C.s3,
                          color: player.financials.scholarship === 'Full' ? C.gold : C.t3,
                        }}>
                          {player.financials.scholarship}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t3 }}>
                        {player.financials.nilValue > 0 ? formatCurrency(player.financials.nilValue) : '--'}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t2 }}>
                        {formatCurrency(player.financials.totalCost)}
                      </td>
                    </tr>
                    {/* Expanded Evaluation Detail */}
                    {isExpanded && ev && evStats && (
                      <tr>
                        <td colSpan={COL_COUNT} style={{ padding: '0 16px 16px', background: C.s1, borderTop: `1px solid ${C.border}` }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 12 }}>
                            {/* Key Insight */}
                            <div style={{ padding: '12px 16px', border: `1px solid ${C.goldDim}`, borderRadius: 10, fontSize: 12, lineHeight: 1.6 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <Zap size={10} style={{ color: C.gold }} />
                                <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' }}>Key Insight</span>
                              </div>
                              <span style={{ color: C.t2 }}>{ev.keyInsight}</span>
                            </div>

                            {/* Stat Detail Grid */}
                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                              {[
                                { l: 'FG%', v: `${evStats.fgPct}%` }, { l: '3P%', v: `${evStats.fg3Pct}%` },
                                { l: 'FT%', v: `${evStats.ftPct}%` }, { l: 'SPG', v: evStats.spg?.toFixed(1) },
                                { l: 'BPG', v: evStats.bpg?.toFixed(1) }, { l: 'EFF', v: evStats.efficiency?.toFixed(1) },
                                { l: 'USG%', v: `${evStats.usagePct?.toFixed(1)}` }, { l: 'L5 PPG', v: evStats.last5Ppg?.toFixed(1) },
                              ].map(s => (
                                <div key={s.l} style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 500, color: C.text }}>{s.v || '--'}</div>
                                  <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: 'uppercase', color: C.t3, marginTop: 2 }}>{s.l}</div>
                                </div>
                              ))}
                            </div>

                            {/* Strengths / Weaknesses / Development */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {[
                                { title: 'Strengths', items: ev.strengths, color: C.elite, icon: CheckCircle2 },
                                { title: 'Weaknesses', items: ev.weaknesses, color: C.dev, icon: AlertTriangle },
                                { title: 'Development', items: ev.developmentNeeds, color: C.gen, icon: TrendingUp },
                              ].map(section => (
                                <div key={section.title}>
                                  <div style={{ fontSize: 9, fontWeight: 600, color: section.color, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                                    {section.title}
                                  </div>
                                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {section.items.map((item, j) => (
                                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: C.t2, lineHeight: 1.5 }}>
                                        <section.icon size={10} style={{ color: section.color, marginTop: 2, flexShrink: 0 }} />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>

                            {/* Bottom badges + View Profile link */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, flexWrap: 'wrap' }}>
                              {[
                                { label: 'NBA Projection', badge: <NBAProjectionBadge projection={ev.nbaProjection} /> },
                                { label: 'Portal Risk', badge: <PortalRiskBadge risk={ev.portalRisk} /> },
                                { label: 'Starter Rate', badge: <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.t2 }}>{evStats.starterRate?.toFixed(0)}%</span> },
                              ].map(b => (
                                <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', color: C.t3 }}>{b.label}:</span>
                                  {b.badge}
                                </div>
                              ))}
                              <button
                                onClick={(e) => { e.stopPropagation(); onPlayerClick?.(player.playerName); }}
                                style={{
                                  marginLeft: 'auto', padding: '4px 12px', borderRadius: 6, fontSize: 10, fontWeight: 500,
                                  background: C.goldDim, color: C.gold, border: 'none', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                }}
                              >
                                View Profile <ChevronRight size={10} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {players.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.goldDim}`, background: C.g08 }}>
                <td style={{ padding: '10px 12px' }} />
                <td style={{ padding: '10px 12px', fontSize: 11, fontWeight: 600, color: C.text, letterSpacing: 0.5 }}>
                  TOTAL ({totals.totalPlayers} players)
                </td>
                <td colSpan={COL_COUNT - 4} />
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11, color: C.t2 }}>--</td>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11, color: C.gold, fontWeight: 600 }}>
                  {formatCurrency(totals.totalCost || 0)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Cap Utilization */}
      {totals.totalCost !== undefined && (
        <div style={{ textAlign: 'right', fontSize: 10, fontFamily: 'monospace', color: C.t3 }}>
          Cap: {formatCurrency(totals.totalCost || 0)} / {formatCurrency(totals.revenueShareCap || REVENUE_SHARE_CAP)}
          ({totals.capUtilization || 0}% utilized)
        </div>
      )}
    </div>
  );
}


function NILTab() {
  // Mock budget data — replace with API when connected
  const TIERS = [
    { label: 'Star Players', budget: 1_400_000, spent: 1_100_000, players: 3, color: '#E8B800' },
    { label: 'Rotation', budget: 600_000, spent: 480_000, players: 6, color: '#60A5FA' },
    { label: 'Depth', budget: 200_000, spent: 120_000, players: 4, color: '#6B7280' },
  ];
  const totalBudget = TIERS.reduce((s, t) => s + t.budget, 0);
  const totalSpent = TIERS.reduce((s, t) => s + t.spent, 0);
  const remaining = totalBudget - totalSpent;

  // ROI framework metrics
  const ROI_METRICS = [
    { label: 'Cost Per Win', value: formatCurrency(Math.round(totalSpent / 18)), sub: 'est. based on 18 projected wins', icon: TrendingUp },
    { label: 'Avg Deal / Starter', value: formatCurrency(Math.round(1_100_000 / 3)), sub: 'across 3 star-tier players', icon: Star },
    { label: 'Budget Utilization', value: `${Math.round((totalSpent / totalBudget) * 100)}%`, sub: `${formatCurrency(remaining)} remaining`, icon: BarChart3 },
    { label: 'Rev Share Headroom', value: formatCurrency(REVENUE_SHARE_CAP - totalSpent), sub: `vs. $${(REVENUE_SHARE_CAP / 1_000_000).toFixed(0)}M cap`, icon: DollarSign },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>NIL & Revenue Sharing</div>

      {/* ROI KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {ROI_METRICS.map(({ label, value, sub, icon: Icon }) => (
          <div key={label} style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Icon size={13} style={{ color: C.gold }} />
              <span style={{ fontSize: 9, fontWeight: 600, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>{label}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 2 }}>{value}</div>
            <div style={{ fontSize: 10, color: C.t2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Tier Budget Allocation */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Budget Allocation by Tier
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {TIERS.map((tier) => {
            const pct = Math.round((tier.spent / tier.budget) * 100);
            return (
              <div key={tier.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{tier.label}</span>
                    <span style={{ fontSize: 10, color: C.t3 }}>{tier.players} players</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{formatCurrency(tier.spent)}</span>
                    <span style={{ fontSize: 10, color: C.t3 }}> / {formatCurrency(tier.budget)}</span>
                  </div>
                </div>
                <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: tier.color, borderRadius: 3, transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: C.t3 }}>{pct}% utilized</span>
                  <span style={{ fontSize: 9, color: C.t3 }}>{formatCurrency(tier.budget - tier.spent)} remaining</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ROI Framework */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            label: 'Deal ROI Scoring',
            desc: 'Each NIL deal is scored against on-court production (PPG + RPG + APG per dollar), retention likelihood, and national exposure value. Connects to SLAM Fit Score.',
          },
          {
            label: 'Market Benchmarking',
            desc: 'AI-estimated fair market values based on comparable deals across your conference, social media reach, conference visibility, and statistical production percentiles.',
          },
          {
            label: 'Collective Integration',
            desc: 'When connected, aggregates payment data from institutional collectives and boosters for real-time burn rate, runway, and settlement cap compliance tracking.',
          },
        ].map((item) => (
          <div key={item.label} style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
              {item.label}
            </div>
            <p style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComplianceTab() {
  // Mock compliance status — replace with live data when connected
  const CATEGORIES = [
    { label: 'NCAA Rules', status: 'ok', score: 100, items: ['Scholarship count: 12/13', 'APR: 970 (above threshold)', 'Practice hours: compliant', 'Contact period: inactive'] },
    { label: 'Revenue Sharing', status: 'warning', score: 78, items: ['Cap utilization: $1.7M / $22M', 'Payment docs: 2 pending', 'Title IX parity: review needed', 'Settlement filing: on track'] },
    { label: 'NIL Disclosure', status: 'ok', score: 94, items: ['Active filings: 11/13 current', 'Pay-for-play flags: 0', 'Collective separation: verified', 'Deal disclosures: 2 due 03/15'] },
    { label: 'Transfer Portal', status: 'ok', score: 100, items: ['Portal window: closed', 'Incoming waivers: 0 pending', 'Eligibility verified: 3/3', 'Sit-out waivers: N/A'] },
    { label: 'Academic', status: 'alert', score: 61, items: ['Team GPA: 2.9 (watch)', '2 players at-risk for eligibility', 'PTD compliance: 9/13', 'Tutoring referrals: 2 active'] },
    { label: 'Health & Safety', status: 'ok', score: 100, items: ['Concussion protocols: current', 'Annual physicals: 13/13', 'Mental health screens: complete', 'Load management logs: up to date'] },
  ];

  const STATUS_STYLE = {
    ok:      { color: '#22C55E', label: 'Clear',   bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.25)' },
    warning: { color: '#F59E0B', label: 'Review',  bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)' },
    alert:   { color: '#EF4444', label: 'Action',  bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)' },
  };

  const overallScore = Math.round(CATEGORIES.reduce((s, c) => s + c.score, 0) / CATEGORIES.length);
  const alerts = CATEGORIES.filter((c) => c.status === 'alert').length;
  const warnings = CATEGORIES.filter((c) => c.status === 'warning').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Compliance Dashboard</div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Overall Score</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: overallScore >= 90 ? '#22C55E' : overallScore >= 70 ? '#F59E0B' : '#EF4444', lineHeight: 1 }}>{overallScore}%</div>
          <div style={{ fontSize: 10, color: C.t2, marginTop: 4 }}>across {CATEGORIES.length} categories</div>
        </div>
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: '#EF4444', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Action Required</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#EF4444', lineHeight: 1 }}>{alerts}</div>
          <div style={{ fontSize: 10, color: C.t2, marginTop: 4 }}>categor{alerts !== 1 ? 'ies' : 'y'} need attention</div>
        </div>
        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: '#F59E0B', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Under Review</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#F59E0B', lineHeight: 1 }}>{warnings}</div>
          <div style={{ fontSize: 10, color: C.t2, marginTop: 4 }}>pending documentation</div>
        </div>
      </div>

      {/* Category Detail Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CATEGORIES.map((cat) => {
          const s = STATUS_STYLE[cat.status];
          return (
            <div key={cat.label} style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' }}>{cat.label}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 6, padding: '2px 7px' }}>
                  {s.label}
                </span>
              </div>
              {/* Score bar */}
              <div style={{ height: 3, background: C.border, borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${cat.score}%`, background: s.color, borderRadius: 2 }} />
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {cat.items.map((item) => (
                  <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <CheckCircle2 size={10} style={{ color: s.color, flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Revenue sharing cap bar */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' }}>Revenue Sharing Cap Utilization</span>
          <span style={{ fontSize: 11, color: C.t3 }}>{formatCurrency(1_700_000)} / {formatCurrency(REVENUE_SHARE_CAP)}</span>
        </div>
        <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.round((1_700_000 / REVENUE_SHARE_CAP) * 100)}%`, background: '#22C55E', borderRadius: 3 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <span style={{ fontSize: 10, color: C.t3 }}>{Math.round((1_700_000 / REVENUE_SHARE_CAP) * 100)}% utilized</span>
          <span style={{ fontSize: 10, color: '#22C55E', fontWeight: 600 }}>{formatCurrency(REVENUE_SHARE_CAP - 1_700_000)} headroom remaining</span>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab({ summary }) {
  const teamRecord = summary?.teamRecord;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Team Analytics</span>
        {(!teamRecord || teamRecord.noData) && (
          <span style={{ fontSize: 10, color: C.t3 }}>Select a team above</span>
        )}
      </div>

      {teamRecord && !teamRecord.noData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard label="Win %" value={`${teamRecord.gamesPlayed > 0 ? ((teamRecord.wins / teamRecord.gamesPlayed) * 100).toFixed(0) : 0}%`} subtext={`${teamRecord.wins}-${teamRecord.losses}`} icon={TrendingUp} />
          <KPICard label="Points Per Game" value={teamRecord.avgPoints} subtext={`Opp: ${teamRecord.avgOppPoints}`} icon={BarChart3} />
          <KPICard label="Point Differential" value={`${teamRecord.pointDiff >= 0 ? '+' : ''}${teamRecord.pointDiff}`} subtext="Per game avg" icon={teamRecord.pointDiff >= 0 ? TrendingUp : TrendingDown} trend={teamRecord.pointDiff >= 0 ? 'up' : 'down'} />
          <KPICard label="Games Played" value={teamRecord.gamesPlayed} subtext="Current season" icon={Target} />
        </div>
      ) : (
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
          <BarChart3 size={28} style={{ margin: '0 auto 12px', color: C.t3, display: 'block' }} />
          <p style={{ fontSize: 12, color: C.t2 }}>
            Select a team to view analytics data from NatStat.
          </p>
          <p style={{ fontSize: 10, marginTop: 4, color: C.t3 }}>
            MBB data is refreshed daily via Cloud Scheduler.
          </p>
        </div>
      )}

      {/* Four Factors placeholder */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Four Factors Analysis
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {['eFG%', 'Turnover Rate', 'ORB%', 'FT Rate'].map(factor => (
            <div key={factor} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 200, color: C.t2, letterSpacing: -1 }}>--</div>
              <div style={{ fontSize: 8, color: C.t3, letterSpacing: 2, marginTop: 4 }}>{factor}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 10, marginTop: 16, color: C.t3 }}>
          Phase 2: Advanced metrics computed from NatStat play-by-play data.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EVALUATIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function GradeBadge({ grade }) {
  const gradeColors = {
    'A+': C.gen, 'A': C.gen, 'A-': C.gen,
    'B+': C.elite, 'B': C.elite, 'B-': C.elite,
    'C+': C.gold, 'C': C.gold, 'C-': C.gold,
    'D+': C.dev, 'D': C.dev,
  };
  const color = gradeColors[grade] || C.t3;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: 8, fontSize: 11, fontWeight: 600, fontFamily: 'monospace',
      background: `${color}18`, color, border: `1px solid ${color}33`,
    }}>
      {grade}
    </span>
  );
}

function RoleBadge({ role }) {
  const roleConfig = {
    Star: C.gold,
    'Secondary Option': C.gen,
    Rotation: C.elite,
    Developmental: C.t2,
    Specialist: C.hdi,
  };
  const color = roleConfig[role] || C.t3;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
      background: `${color}18`, color, border: `1px solid ${color}33`,
    }}>
      {role}
    </span>
  );
}

function PortalRiskBadge({ risk }) {
  const colors = { Low: C.elite, Medium: C.gold, High: C.dev };
  const color = colors[risk] || C.gold;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
      background: `${color}18`, color,
    }}>
      {risk}
    </span>
  );
}

function NBAProjectionBadge({ projection }) {
  const colors = { 'Lottery Potential': C.gold, 'Draft Prospect': C.gen, Fringe: C.t2, None: C.t3 };
  const color = colors[projection] || C.t3;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
      background: `${color}18`, color,
    }}>
      {projection}
    </span>
  );
}

function PlayerEvaluationCard({ player, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { evaluation, stats } = player;
  const fitColor = evaluation.fitScore >= 80 ? C.elite : evaluation.fitScore >= 60 ? C.gold : C.dev;

  return (
    <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14, transition: 'all .3s' }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{ width: '100%', textAlign: 'left', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: 'none', border: 'none', color: C.text }}
      >
        <GradeBadge grade={evaluation.overallGrade} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{player.playerName}</span>
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.t2 }}>{player.position} #{player.jersey}</span>
            <RoleBadge role={evaluation.role} />
          </div>
          <div style={{ fontSize: 10, marginTop: 2, color: C.t2 }}>
            {stats.ppg} PPG | {stats.rpg} RPG | {stats.apg} APG | {stats.mpg} MPG | {stats.gamesPlayed} GP
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {/* Fit Score — thin SLAM bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 60, height: 3, background: C.s3, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${evaluation.fitScore}%`, background: fitColor, borderRadius: 2, transition: 'width .6s ease' }} />
            </div>
            <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 500, color: fitColor }}>{evaluation.fitScore}</span>
          </div>
          <ChevronDown size={12} style={{ color: C.t3, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .3s' }} />
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Key Insight */}
          <div style={{ marginTop: 12, padding: '12px 16px', border: `1px solid ${C.goldDim}`, borderRadius: 10, fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Zap size={10} style={{ color: C.gold }} />
              <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' }}>Key Insight</span>
            </div>
            <span style={{ color: C.t2 }}>{evaluation.keyInsight}</span>
          </div>

          {/* Stat Detail Row */}
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {[
              { l: 'FG%', v: `${stats.fgPct}%` }, { l: '3P%', v: `${stats.fg3Pct}%` },
              { l: 'FT%', v: `${stats.ftPct}%` }, { l: 'SPG', v: stats.spg?.toFixed(1) },
              { l: 'BPG', v: stats.bpg?.toFixed(1) }, { l: 'EFF', v: stats.efficiency?.toFixed(1) },
              { l: 'USG%', v: `${stats.usagePct?.toFixed(1)}` }, { l: 'L5 PPG', v: stats.last5Ppg?.toFixed(1) },
            ].map(s => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 500, color: C.text }}>{s.v || '--'}</div>
                <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: 'uppercase', color: C.t3, marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Strengths, Weaknesses, Development */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { title: 'Strengths', items: evaluation.strengths, color: C.elite, icon: CheckCircle2 },
              { title: 'Weaknesses', items: evaluation.weaknesses, color: C.dev, icon: AlertTriangle },
              { title: 'Development', items: evaluation.developmentNeeds, color: C.gen, icon: TrendingUp },
            ].map(section => (
              <div key={section.title}>
                <div style={{ fontSize: 9, fontWeight: 600, color: section.color, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                  {section.title}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {section.items.map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: C.t2, lineHeight: 1.5 }}>
                      <section.icon size={10} style={{ color: section.color, marginTop: 2, flexShrink: 0 }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, flexWrap: 'wrap' }}>
            {[
              { label: 'NBA Projection', badge: <NBAProjectionBadge projection={evaluation.nbaProjection} /> },
              { label: 'Portal Risk', badge: <PortalRiskBadge risk={evaluation.portalRisk} /> },
              { label: 'Starter Rate', badge: <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.t2 }}>{stats.starterRate?.toFixed(0)}%</span> },
            ].map(b => (
              <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', color: C.t3 }}>{b.label}:</span>
                {b.badge}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// GUIDED TOUR STEPS — sport-aware
// ═══════════════════════════════════════════════════════════════════════════

const BASKETBALL_TOUR_STEPS = [
  { targetSelector: '[data-tour="gm-header"]', title: 'GM Command Center', body: 'Your front office dashboard — roster economics, cap management, compliance, and recruiting analytics all in one place.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-team-search"]', title: 'Switch Teams', body: 'Search for any NCAA team to view their roster, financials, and portal activity. Great for scouting opponents.', placement: 'left' },
  { targetSelector: '[data-tour="gm-tab-bar"]', title: 'Dashboard Tabs', body: 'Navigate between Overview, Roster (with AI evaluations), Portal Intel, NIL, Compliance, and Analytics.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-kpi-cards"]', title: 'Key Metrics', body: 'At-a-glance KPIs — scholarship count, cap utilization, portal targets, and compliance score.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-cap-bar"]', title: 'Revenue Share Cap', body: 'Track your $22M revenue share cap usage. See how scholarship costs, NIL deals, and revenue share allocations break down.', placement: 'top' },
  { targetSelector: '[data-tour="gm-quick-access"]', title: 'Quick Access', body: 'Jump to any section directly. Each card opens a dedicated view with deeper analytics.', placement: 'top' },
];

const BASEBALL_TOUR_STEPS = [
  { targetSelector: '[data-tour="gm-header"]', title: 'Baseball Operations', body: 'Your front office command center — roster management, scholarship allocation, and player pipeline tracking.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-team-search"]', title: 'Switch Programs', body: 'Search any NCAA baseball program to analyze their roster, pitching staff, and recruiting class.', placement: 'left' },
  { targetSelector: '[data-tour="gm-tab-bar"]', title: 'Dashboard Tabs', body: 'Navigate between Overview, Roster (with AI evaluations), Portal Intel, NIL, Compliance, and Analytics.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-kpi-cards"]', title: 'Program Metrics', body: 'Track scholarship count (11.7 limit), pitching staff depth, position player coverage, and compliance status.', placement: 'bottom' },
  { targetSelector: '[data-tour="gm-cap-bar"]', title: 'Budget Overview', body: 'Monitor your NIL and revenue share allocation across position players, pitchers, and two-way athletes.', placement: 'top' },
  { targetSelector: '[data-tour="gm-quick-access"]', title: 'Quick Access', body: 'Jump to roster composition, portal intel for arms and bats, NIL management, or advanced analytics.', placement: 'top' },
];

export default function GMDashboard({ initialTab }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTeam: contextTeam } = useTeam();

  // Detect sport from URL path (e.g. /baseball/gm → 'baseball')
  const sport = useMemo(() => {
    const seg = location.pathname.split('/')[1];
    return ['basketball', 'baseball', 'soccer', 'hockey', 'football', 'golf'].includes(seg) ? seg : 'basketball';
  }, [location.pathname]);
  const isBaseball = sport === 'baseball';

  // Guided tour — sport-aware steps
  const tourSteps = isBaseball ? BASEBALL_TOUR_STEPS : BASKETBALL_TOUR_STEPS;
  const gmTour = useGuidedTour(`gm-dashboard-${sport}`, tourSteps);

  // Derive team name from sport selector context
  const contextTeamName = contextTeam?.teamName || contextTeam?.team?.team_name || contextTeam?.team?.full_name || '';

  const [activeTab, setActiveTab] = useState(initialTab || searchParams.get('tab') || 'overview');

  // Sync activeTab when initialTab prop changes (sidebar navigation)
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [summary, setSummary] = useState(null);
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(contextTeamName);
  const [teamInput, setTeamInput] = useState(contextTeamName);
  const [error, setError] = useState(null);
  const [headshotMap, setHeadshotMap] = useState({});
  const [evaluationsData, setEvaluationsData] = useState(null);
  const [evaluationsLoading, setEvaluationsLoading] = useState(false);
  const [evaluationsError, setEvaluationsError] = useState(null);
  const [evaluationsRefreshKey, setEvaluationsRefreshKey] = useState(0);

  // Sync from sport selector when user changes team externally
  useEffect(() => {
    if (contextTeamName && contextTeamName !== selectedTeam) {
      setSelectedTeam(contextTeamName);
      setTeamInput(contextTeamName);
      setLoading(true);
    }
  }, [contextTeamName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce team search input (500ms)
  useEffect(() => {
    const timer = setTimeout(() => setSelectedTeam(teamInput), 500);
    return () => clearTimeout(timer);
  }, [teamInput]);

  // Fetch GM summary on mount
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const url = selectedTeam
          ? `/api/basketball/gm/summary?league=MBB&team=${encodeURIComponent(selectedTeam)}`
          : '/api/basketball/gm/summary?league=MBB';
        const res = await fetch(url);
        const data = await res.json();
        const payload = data.data || data;
        if (data.success !== false) {
          setSummary(payload);
        }
      } catch (err) {
        console.error('[GM Dashboard] Summary fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [selectedTeam]);

  // Fetch roster when roster tab is active and team is selected (skip if already loaded for this team)
  useEffect(() => {
    if ((activeTab === 'roster' || activeTab === 'overview') && selectedTeam) {
      if (roster?.teamName === selectedTeam || roster?.team === selectedTeam) return;
      setRosterLoading(true);
      const fetchRoster = async () => {
        try {
          const res = await fetch(`/api/basketball/gm/roster?league=MBB&team=${encodeURIComponent(selectedTeam)}`);
          const data = await res.json();
          const payload = data.data || data;
          if (data.success !== false) {
            setRoster(payload);
          }
        } catch (err) {
          console.error('[GM Dashboard] Roster fetch error:', err);
        } finally {
          setRosterLoading(false);
        }
      };
      fetchRoster();
    }
  }, [activeTab, selectedTeam, roster?.teamName, roster?.team]);

  // Fetch ESPN headshot map when team changes
  useEffect(() => {
    const espnId = getEspnTeamId(selectedTeam);
    if (!espnId) { setHeadshotMap({}); return; }

    fetchTeamHeadshots(espnId, 'mens')
      .then(map => setHeadshotMap(map))
      .catch(err => console.warn('[GM] Headshot map fetch failed:', err));
  }, [selectedTeam]);

  // Fetch evaluations when roster tab is active and team is selected
  useEffect(() => {
    if (activeTab === 'roster' && selectedTeam) {
      // Skip re-fetch if data already loaded for this team (unless refresh requested)
      if (evaluationsData && evaluationsData.teamName === selectedTeam && evaluationsRefreshKey === 0) {
        return;
      }
      setEvaluationsLoading(true);
      setEvaluationsError(null);
      const fetchEvaluations = async () => {
        try {
          const refreshParam = evaluationsRefreshKey > 0 ? '&refresh=true' : '';
          const res = await fetch(
            `/api/basketball/gm/evaluations?league=MBB&team=${encodeURIComponent(selectedTeam)}${refreshParam}`
          );
          const data = await res.json();
          if (data.success) {
            setEvaluationsData(data.data);
          } else {
            setEvaluationsError(data.error?.message || 'Failed to generate evaluations');
          }
        } catch (err) {
          console.error('[GM Dashboard] Evaluations fetch error:', err);
          setEvaluationsError(err.message);
        } finally {
          setEvaluationsLoading(false);
        }
      };
      fetchEvaluations();
    }
  }, [activeTab, selectedTeam, evaluationsRefreshKey]);

  const handlePlayerClick = (playerName) => {
    navigate(`/basketball/gm/player/${encodeURIComponent(playerName)}`);
  };

  const handleEvaluationsRefresh = () => {
    setEvaluationsData(null);
    setEvaluationsError(null);
    setEvaluationsRefreshKey(k => k + 1);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab summary={summary} onNavigate={setActiveTab} />;
      case 'roster':
        return (
          <RosterTab
            roster={roster}
            loading={rosterLoading}
            headshotMap={headshotMap}
            onPlayerClick={handlePlayerClick}
            evaluationsData={evaluationsData}
            evaluationsLoading={evaluationsLoading}
            evaluationsError={evaluationsError}
            selectedTeam={selectedTeam}
            onEvalRefresh={handleEvaluationsRefresh}
          />
        );
      case 'diamond-eye':
        return (
          <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256, color: '#666' }}><Loader2 size={20} className="animate-spin" style={{ marginRight: 8 }} /> Loading Diamond Eye...</div>}>
            <div style={{ height: 'calc(100vh - 200px)', borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}` }}>
              <GameFilmDashboard embedded />
            </div>
          </Suspense>
        );
      case 'portal':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <PortalIntelTab sport={sport} embedded />
            {sport === 'basketball' && <TransferPortal embedded />}
          </div>
        );
      case 'nil':
        return <NILTab />;
      case 'compliance':
        return <ComplianceTab />;
      case 'analytics':
        return <AnalyticsTab summary={summary} />;
      default:
        return <OverviewTab summary={summary} onNavigate={setActiveTab} />;
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 24px 60px', color: C.text, fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* Header */}
      <div data-tour="gm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28, borderBottom: `1px solid ${C.border}`, paddingBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.gold }} />
            <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 3, textTransform: 'uppercase' }}>
              {isBaseball ? 'Baseball Operations' : 'General Manager'}
            </span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 200, letterSpacing: -1, lineHeight: 1.1 }}>
            {summary?.rosterOverview?.teamName || (isBaseball ? 'NCAA Baseball' : 'NCAA Men\'s Basketball')}
          </div>
          <div style={{ fontSize: 11, color: C.t2, marginTop: 6 }}>
            {isBaseball ? 'Pitching Staff · Roster Depth · Operations' : 'Roster Economics & Operations'}
          </div>
        </div>

        {/* Team Selector */}
        <div data-tour="gm-team-search" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            value={teamInput}
            onChange={(e) => setTeamInput(e.target.value)}
            placeholder={isBaseball ? 'Search program...' : 'Search team...'}
            style={{
              padding: '8px 14px', borderRadius: 100, fontSize: 12,
              background: 'transparent', border: `1px solid ${C.border}`,
              color: C.text, width: 200, outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSelectedTeam(teamInput);
                setLoading(true);
              }
            }}
          />
          {loading && <Loader2 size={14} className="animate-spin" style={{ color: C.gold }} />}
        </div>
      </div>

      {/* Tab Bar — SLAM Pill style */}
      <div data-tour="gm-tab-bar" style={{ display: 'flex', gap: 6, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
        {TABS.map((tab) => (
          <Pill key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </Pill>
        ))}
      </div>

      {/* Error Banner */}
      {error && !summary && (
        <div style={{ borderRadius: 14, padding: 16, marginBottom: 16, border: `1px solid ${C.dev}22`, background: `${C.dev}08`, color: C.dev, fontSize: 12 }}>
          Failed to load dashboard data: {error}
        </div>
      )}

      {/* Tab Content */}
      <div style={{ minHeight: 400 }}>
        {loading && !summary ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 80 }}>
            <Loader2 size={20} className="animate-spin" style={{ color: C.gold }} />
          </div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* Guided Tour Tooltip */}
      {gmTour.currentStep && (
        <GuidedTooltip
          targetSelector={gmTour.currentStep.targetSelector}
          title={gmTour.currentStep.title}
          body={gmTour.currentStep.body}
          placement={gmTour.currentStep.placement}
          step={gmTour.stepIndex + 1}
          totalSteps={gmTour.totalSteps}
          onNext={gmTour.next}
          onDismiss={gmTour.dismiss}
        />
      )}
    </div>
  );
}
